import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  Role,
  TourOperator,
  TourOperatorCreateRequest,
  TourOperatorUpdateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getDropdownOptions } from '@/modules/dataParser.js';
import {
  buildAbstractMap,
  toBoolean,
  toOptionalNumber,
} from './common.ts';

type LegacyTourOperator = TourOperator & {
  isActive?: boolean | null;
};

type LegacyTourOperatorPayload = Partial<LegacyTourOperator> & Record<string, unknown>;

function normalizeTourOperator(
  entry: TourOperator | null | undefined,
  showOnlyActive?: boolean
): LegacyTourOperator | undefined {
  if (!entry) {
    return undefined;
  }

  return {
    ...entry,
    isActive: showOnlyActive ?? entry.isActive ?? null,
  };
}

function buildCreateRequest(data: LegacyTourOperatorPayload): TourOperatorCreateRequest {
  return {
    ...data,
    isBlobProcessingPaused: toBoolean(data.isBlobProcessingPaused),
    isPaxDataUploadEnabled: toBoolean(data.isPaxDataUploadEnabled),
    isPaxListDownloadEnabled: toBoolean(data.isPaxListDownloadEnabled),
    isPoolingEnabled: toBoolean(data.isPoolingEnabled),
    passengerUploadInterval: toOptionalNumber(data.passengerUploadInterval),
    sendMailOnScheduleChange: toBoolean(data.sendMailOnScheduleChange),
    showUnpublishedFares: toBoolean(data.showUnpublishedFares),
    splitPassengerData: toBoolean(data.splitPassengerData),
    threshold: toOptionalNumber(data.threshold),
    usesOnlyNewAndCanBkgStatusInPaxDataUpload: toBoolean(data.usesOnlyNewAndCanBkgStatusInPaxDataUpload),
  } as TourOperatorCreateRequest;
}

function buildUpdateRequest(data: LegacyTourOperatorPayload): TourOperatorUpdateRequest {
  return {
    ...data,
    isBlobProcessingPaused: toBoolean(data.isBlobProcessingPaused),
    isPaxDataUploadEnabled: toBoolean(data.isPaxDataUploadEnabled),
    isPaxListDownloadEnabled: toBoolean(data.isPaxListDownloadEnabled),
    isPoolingEnabled: toBoolean(data.isPoolingEnabled),
    passengerUploadInterval: toOptionalNumber(data.passengerUploadInterval),
    sendMailOnScheduleChange: toBoolean(data.sendMailOnScheduleChange),
    showUnpublishedFares: toBoolean(data.showUnpublishedFares),
    splitPassengerData: toBoolean(data.splitPassengerData),
    threshold: toOptionalNumber(data.threshold),
    usesOnlyNewAndCanBkgStatusInPaxDataUpload: toBoolean(data.usesOnlyNewAndCanBkgStatusInPaxDataUpload),
  } as TourOperatorUpdateRequest;
}

function buildRolesMap(data: Role[] | null | undefined): Record<string, string> {
  return Object.fromEntries(
    (data ?? [])
      .filter((entry): entry is Role & { id: number } => entry.id !== null && entry.id !== undefined)
      .map(entry => [String(entry.id), String(entry.name ?? '')])
  );
}

export const useTourOperatorsStore = defineStore('tourOperators', () => {
  const apiClient = useApiClient();

  const list = ref<LegacyTourOperator[]>([]);
  const abstract = ref<Record<string, string>>({});
  const added = ref<LegacyTourOperator | null>(null);
  const loading = ref<boolean>(true);
  const roles = ref<Record<string, string> | null>(null);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function getRoles(): Promise<Record<string, string>> {
    const response = await apiClient.tourOperatorRole.get();
    roles.value = buildRolesMap(response);
    return roles.value;
  }

  async function getCurrent(): Promise<TourOperator> {
    const response = await apiClient.tourOperator.current.get();
    return response ?? ({} as TourOperator);
  }

  async function getTourOperatorById(id: number): Promise<LegacyTourOperator | undefined> {
    const response = await apiClient.tourOperator.byId(id).get();
    return normalizeTourOperator(response);
  }

  async function add(data: LegacyTourOperatorPayload): Promise<LegacyTourOperator | undefined> {
    const response = await apiClient.tourOperator.post(buildCreateRequest(data));
    added.value = normalizeTourOperator(response) ?? null;
    return added.value ?? undefined;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.tourOperator.byId(id).byIsActive(active).post();
  }

  async function update(data: LegacyTourOperatorPayload): Promise<ArrayBuffer | undefined> {
    return await apiClient.tourOperator.put(buildUpdateRequest(data));
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<LegacyTourOperator[]> {
    loading.value = true;

    try {
      const response = await apiClient.tourOperator.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = (response ?? [])
        .map(entry => normalizeTourOperator(entry, showOnlyActive))
        .filter((entry): entry is LegacyTourOperator => Boolean(entry));
      abstract.value = buildAbstractMap(list.value, entry => entry.name);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  async function getAbstract({ showOnlyActive = true }: { showOnlyActive?: boolean } = {}): Promise<Record<string, string>> {
    loading.value = true;

    try {
      const response = await apiClient.tourOperator.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      abstract.value = buildAbstractMap(response ?? [], entry => entry.name);
      return abstract.value;
    } finally {
      loading.value = false;
    }
  }

  function getById({ id }: { id: string | number }): string | null {
    return abstract.value[String(id)] ?? null;
  }

  return {
    list,
    abstract,
    added,
    loading,
    roles,
    options,
    getRoles,
    getCurrent,
    getTourOperatorById,
    add,
    toggle,
    update,
    get,
    getAbstract,
    getById,
  };
});
